import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../utils/api';
import './CyclePaymentModal.css';

/**
 * CyclePaymentModal
 *
 * Modal to initiate a new paid cycle.
 * All cycles require payment via Paystack.
 *
 * Props:
 *   isOpen          {boolean}   — whether to show the modal
 *   onClose         {function}  — called when modal should close without action
 *   onSuccess       {function}  — called when cycle is successfully started
 *   required        {boolean}   — if true, user cannot close modal without paying (disables close button)
 */
export default function CyclePaymentModal({ isOpen, onClose, onSuccess, required = false }) {
    // 'loading' | 'redirect' | 'success' | 'error'
    const [status, setStatus] = useState('loading');
    const [cycleData, setCycleData] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setStatus('loading');
        setCycleData(null);
        setErrorMsg('');

        api.post('/payments/check-cycle-status')
            .then(({ data }) => {
                const d = data.data;
                setCycleData({
                    nextCycleNumber: d.nextCycleNumber,
                    paymentPageUrl: d.paymentPageUrl,
                    alreadyUnlocked: d.alreadyUnlocked,
                });
                // If already unlocked, proceed directly to success
                if (d.alreadyUnlocked) {
                    setStatus('success');
                    onSuccess(null);
                } else {
                    setStatus('redirect');
                }
            })
            .catch((err) => {
                setErrorMsg(err.response?.data?.message || 'Failed to prepare payment. Please try again.');
                setStatus('error');
            });
    }, [isOpen, onSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleProceedToPaystack = () => {
        if (cycleData?.paymentPageUrl) {
            // HIGH: Generate reference and store in sessionStorage for callback validation
            const reference = `ref_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            sessionStorage.setItem('paystack_reference', reference);
            window.location.href = cycleData.paymentPageUrl;
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="cpm-overlay" role="dialog" aria-modal="true" aria-label="Start New Cycle">
            <div className="cpm-modal">
                {!required && (
                    <button className="cpm-close" onClick={onClose} aria-label="Close">×</button>
                )}

                <h2 className="cpm-title">
                    Start Cycle {cycleData?.nextCycleNumber || ''}
                </h2>

                {status === 'loading' && (
                    <div className="cpm-loading">
                        <div className="cpm-spinner" />
                        <p>Preparing Cycle {cycleData?.nextCycleNumber}…</p>
                    </div>
                )}

                {status === 'error' && (
                    <div className="cpm-error-state">
                        <p>{errorMsg || 'Something went wrong. Please try again.'}</p>
                        <button className="cpm-btn-cancel" onClick={onClose} disabled={required}>Close</button>
                    </div>
                )}

                {status === 'redirect' && cycleData && (
                    <div className="cpm-paystack">
                        <p className="cpm-paystack-info">
                            You will be taken to Paystack's secure payment page to complete your purchase for Cycle {cycleData.nextCycleNumber}.
                        </p>
                        <div className="cpm-actions">
                            <button className="cpm-btn-cancel" onClick={onClose} disabled={required}>Cancel</button>
                            <button className="cpm-btn-pay" onClick={handleProceedToPaystack}>
                                Pay with Paystack
                            </button>
                        </div>
                    </div>
                )}

                {status === 'success' && (
                    <p style={styles.message}>Cycle unlocked! Proceeding…</p>
                )}
            </div>
        </div>,
        document.body
    );
}

const styles = {
    message: {
        fontSize: '1rem',
        color: '#2d6a4f',
        marginBottom: '1.2rem',
    },
};